import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Chip,
  LinearProgress,
  Alert,
  Fab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { fetchBudgets, createBudget, updateBudget, deleteBudget } from '../store/slices/budgetSlice';

const Budgets = () => {
  const dispatch = useDispatch();
  const { budgets, loading, error } = useSelector(state => ({
    budgets: state.budgets.budgets,
    loading: state.budgets.loading.budgets,
    error: state.budgets.error
  }));
  const { categories } = useSelector(state => state.transactions);
  
  const [open, setOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    budget_amount: '',
    category_id: '',
    period_type: 'monthly',
    description: '',
    alert_threshold: 80
  });

  useEffect(() => {
    dispatch(fetchBudgets());
  }, [dispatch]);

  const handleSubmit = async () => {
    try {
      if (editingBudget) {
        await dispatch(updateBudget({ id: editingBudget.id, data: formData }));
      } else {
        await dispatch(createBudget(formData));
      }
      handleClose();
    } catch (error) {
      console.error('Error saving budget:', error);
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setFormData({
      name: budget.name,
      budget_amount: budget.budget_amount,
      category_id: budget.category_id,
      period_type: budget.period_type,
      description: budget.description || '',
      alert_threshold: budget.alert_threshold
    });
    setOpen(true);
  };

  const handleDelete = async (budgetId) => {
    if (window.confirm('Are you sure you want to delete this budget?')) {
      await dispatch(deleteBudget(budgetId));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingBudget(null);
    setFormData({
      name: '',
      budget_amount: '',
      category_id: '',
      period_type: 'monthly',
      description: '',
      alert_threshold: 80
    });
  };

  const getBudgetStatus = (budget) => {
    const utilization = budget.utilization_percentage || 0;
    if (utilization > 100) return { color: 'error', icon: <WarningIcon />, text: 'Exceeded' };
    if (utilization > budget.alert_threshold) return { color: 'warning', icon: <WarningIcon />, text: 'Warning' };
    return { color: 'success', icon: <CheckCircleIcon />, text: 'On Track' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Loading budgets...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Budget Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          Create Budget
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {budgets.map((budget) => {
          const status = getBudgetStatus(budget);
          const utilization = budget.utilization_percentage || 0;
          
          return (
            <Grid item xs={12} md={6} lg={4} key={budget.id}>
              <Card 
                elevation={3}
                sx={{ 
                  height: '100%',
                  border: `2px solid transparent`,
                  '&:hover': {
                    border: `2px solid ${status.color === 'error' ? '#f44336' : status.color === 'warning' ? '#ff9800' : '#4caf50'}`,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h2" gutterBottom>
                      {budget.name}
                    </Typography>
                    <Box>
                      <Tooltip title="Edit Budget">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEdit(budget)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Budget">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDelete(budget.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box mb={2}>
                    <Chip 
                      icon={status.icon} 
                      label={status.text}
                      color={status.color}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {budget.period_type.charAt(0).toUpperCase() + budget.period_type.slice(1)} Budget
                    </Typography>
                  </Box>

                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        {formatCurrency(budget.current_spent || 0)} spent
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(budget.budget_amount)}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(utilization, 100)}
                      color={status.color}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {utilization.toFixed(1)}% used
                    </Typography>
                  </Box>

                  {budget.description && (
                    <Typography variant="body2" color="text.secondary">
                      {budget.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        {budgets.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No budgets created yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first budget to start tracking your spending goals
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
              >
                Create Your First Budget
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Budget Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBudget ? 'Edit Budget' : 'Create New Budget'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Budget Name"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Budget Amount"
                type="number"
                fullWidth
                value={formData.budget_amount}
                onChange={(e) => setFormData({ ...formData, budget_amount: e.target.value })}
                required
                InputProps={{
                  startAdornment: '$'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Period"
                select
                fullWidth
                value={formData.period_type}
                onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Category"
                select
                fullWidth
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Alert Threshold (%)"
                type="number"
                fullWidth
                value={formData.alert_threshold}
                onChange={(e) => setFormData({ ...formData, alert_threshold: e.target.value })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description (Optional)"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.name || !formData.budget_amount || !formData.category_id}
          >
            {editingBudget ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Budgets; 